import { CloseButton, Dialog, Portal, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

interface MangoModalProps {
  open?: boolean
  isOpen?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  onClose?: () => void
  closeButton?: boolean
  header?: string | React.ReactNode
  body?: string | React.ReactNode
  footer?: string | React.ReactNode
  children?: React.ReactNode
}

export const MangoModal = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  closeButton = true,
  header,
  body,
  footer,
  children,
}: MangoModalProps) => {
  const isOpenCompat = open ?? isOpen

  const handleOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (onOpenChange) {
        onOpenChange(details)
      } else if (onClose && !details.open) {
        onClose()
      }
    },
    [onOpenChange, onClose],
  )

  const handleClose = React.useCallback(() => {
    handleOpenChange({ open: false })
  }, [handleOpenChange])

  const headerContent = useMemo(() => {
    if (!header) return null
    if (typeof header === 'string') {
      return <Text {...textStyles[textStyleKeys.Heading4]}>{header}</Text>
    }
    return header
  }, [header])

  const bodyContent = children ?? body

  return (
    <Dialog.Root open={isOpenCompat ?? false} onOpenChange={handleOpenChange}>
      <Portal>
        <Dialog.Backdrop
          css={{
            position: 'fixed',
            inset: 0,
            bg: 'blackAlpha.600',
            zIndex: 'modal',
          }}
        />
        <Dialog.Positioner>
          <Dialog.Content
            css={{
              bg: 'bg.panel',
              borderRadius: '0.75rem',
              boxShadow: 'lg',
              minW: '24rem',
              maxW: '32rem',
              mx: '1rem',
            }}
          >
            {(headerContent || closeButton) && (
              <Dialog.Header
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '1.5rem',
                  pt: '1.25rem',
                  pb: '0.5rem',
                }}
              >
                {headerContent}
                {closeButton && (
                  <CloseButton
                    onClick={handleClose}
                    size="sm"
                    css={{
                      color: 'fg.muted',
                      _hover: { color: 'fg.default' },
                    }}
                  />
                )}
              </Dialog.Header>
            )}
            <Dialog.Body
              css={{
                px: '1.5rem',
                py: '0.75rem',
              }}
            >
              {bodyContent}
            </Dialog.Body>
            {footer && (
              <Dialog.Footer
                css={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  px: '1.5rem',
                  pb: '1.25rem',
                  pt: '0.5rem',
                }}
              >
                {footer}
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
